import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ArrowRightIcon } from 'lucide-react';
import { navLinks, LOGO_URL } from '../data/navigation';
interface HeaderProps {
  onOpenDemo: () => void;
}
export function Header({
  onOpenDemo
}: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
  }, [isMobileMenuOpen]);
  return <>
      <header className="fixed top-0 left-0 right-0 z-[60] bg-stark-white/90 backdrop-blur-xl border-b border-midnight-navy/10 px-6 py-4" data-id="element-116" data-mp="36b1xibb5zfp4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-8" data-id="element-117" data-mp="3bb69bo14nm90">
          <div className="flex items-center gap-10" data-id="element-118" data-mp="3fayju6a302h0">
            <Link to="/" className="relative z-[70] shrink-0" data-id="element-119" data-mp="3f9nbdt5oy6jk">
              <img src={LOGO_URL} alt="ROI Complete" className="h-12 md:h-16 w-auto object-contain" data-id="element-120" data-mp="2wlm739qzpwhs" />
            </Link>

            <nav className="hidden lg:flex items-center gap-8" data-id="element-121" data-mp="2mlvg3i1n1zmg">
              {navLinks.map(link => {
              const isActive = location.pathname === link.href;
              return <Link key={link.name} to={link.href} className={`relative text-base font-semibold transition-colors duration-300 ${isActive ? 'text-royal-blue' : 'text-midnight-navy/60 hover:text-midnight-navy'}`} data-id="element-122" data-mp="3fblwjxqqtdlq">
                    {link.name}
                    {isActive && <motion.div layoutId="navUnderline" className="absolute -bottom-1.5 left-0 right-0 h-0.5 bg-royal-blue" transition={{
                  type: 'spring',
                  stiffness: 380,
                  damping: 30
                }} data-id="element-123" data-mp="1wfs0utt0p0i" />}
                  </Link>;
            })}
            </nav>
          </div>

          <div className="flex items-center gap-3" data-id="element-124" data-mp="1mmuyd3un7f4t">
            <div className="hidden sm:flex items-center gap-2 mr-2" data-id="element-125" data-mp="1mljruw9tbta0">
              <Link to="/login" className="text-base font-bold text-midnight-navy hover:text-royal-blue px-4 py-2 transition-colors" data-id="element-126" data-mp="18mk55u5v1ogh">
                Log In
              </Link>
              <Link to="/signup" className="text-base font-bold text-stark-white bg-midnight-navy hover:bg-midnight-navy/90 px-5 py-2.5 rounded-full transition-colors" data-id="element-127" data-mp="yq7c8yi53ems">
                Sign Up
              </Link>
            </div>

            <button onClick={onOpenDemo} className="hidden lg:block bg-royal-blue text-stark-white px-6 py-2.5 rounded-full text-base font-bold hover:bg-royal-blue-dark transition-all shadow-lg shadow-royal-blue/20 active:scale-95" data-id="element-128" data-mp="2y29t00zdpuwr">
              Schedule a Consultation
            </button>

            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden relative z-[70] p-2 text-midnight-navy hover:bg-midnight-navy/5 rounded-lg transition-colors" aria-label="Toggle navigation menu" aria-expanded={isMobileMenuOpen} data-id="element-129" data-mp="w610w5r74lw7">
              {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-130" data-mp="1f3lozam4zgfr" /> : <MenuIcon className="w-6 h-6" data-id="element-131" data-mp="1a491qorcs7cn" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence data-id="element-132" data-mp="3gtx2ulqp0r8g">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[50] lg:hidden bg-stark-white" data-id="element-133" data-mp="3gwt3wixu7w14">
            <div className="flex flex-col h-full pt-28 pb-12 px-8" data-id="element-134" data-mp="27zuubofh80mj">
              <nav className="flex flex-col gap-6" data-id="element-135" data-mp="2cy645z5rub4r">
                {navLinks.map((link, idx) => {
              const isActive = location.pathname === link.href;
              return <motion.div key={link.name} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: idx * 0.06
              }} data-id="element-136" data-mp="1k9gzxr9f5pos" data-mpi={idx}>
                      <Link to={link.href} className={`text-3xl font-montserrat font-bold flex items-center justify-between group ${isActive ? 'text-royal-blue' : 'text-midnight-navy'}`} data-id="element-137" data-mp="15j9s05h1fp2c">
                        {link.name}
                        <ArrowRightIcon className={`w-7 h-7 transition-all duration-300 ${isActive ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'}`} data-id="element-138" data-mp="syul813xfexf" />
                      </Link>
                    </motion.div>;
            })}
              </nav>

              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="mt-auto space-y-4" data-id="element-139" data-mp="a8v0fqf4clzt">
                <div className="grid grid-cols-2 gap-3" data-id="element-140" data-mp="3nhr7e4ixkqsu">
                  <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="w-full bg-cool-gray text-midnight-navy py-4 rounded-xl text-center font-bold" data-id="element-141" data-mp="3dt3q6lea68wv">
                    Log In
                  </Link>
                  <Link to="/signup" onClick={() => setIsMobileMenuOpen(false)} className="w-full bg-midnight-navy text-stark-white py-4 rounded-xl text-center font-bold" data-id="element-142" data-mp="38u0rrjst30xv">
                    Sign Up
                  </Link>
                </div>
                <button onClick={() => {
              setIsMobileMenuOpen(false);
              onOpenDemo();
            }} className="w-full bg-royal-blue text-stark-white py-5 rounded-xl text-lg font-bold shadow-2xl shadow-royal-blue/20 active:scale-[0.98] transition-transform" data-id="element-143" data-mp="3mujz0opg62yz">
                  Schedule a Consultation
                </button>
                <div className="mt-8 text-center text-midnight-navy/40 text-sm font-medium" data-id="element-144" data-mp="28sw0a718cn81">
                  © 2026 ROI Complete
                </div>
              </motion.div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}